/**
 * Server state for the templates registry (docs/ux/templates.md): the index,
 * the vocabulary, one base or overlay with its versions, the effective
 * structure, and the writes (save a version, clone, make live). Refusals
 * come back as ApiError with the server's words; the pages show them.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ReportTypeKind } from '@beam/core/status';
import { ApiError, api } from '@/app/api';
import type { ResolvedTemplate, TemplateHead, TemplateOverlay, TemplateVersion, TemplateVersionSummary, TemplateVocabulary, TemplatesIndex, TypeBase } from '@/app/types';
import { doctorKey, doctorsKey } from '../useRegistry';

export const templatesKey = ['templates'] as const;
export const baseKey = (type: string, version?: number) => ['templates', 'base', type, version ?? 'live'] as const;
export const overlayKey = (slug: string, type: string, version?: number) => ['templates', 'overlay', slug, type, version ?? 'live'] as const;
export const resolvedKey = (slug: string, type: string) => ['templates', 'resolved', slug, type] as const;

export type BaseView = { head: TemplateHead; version: TemplateVersion<TypeBase>; versions: TemplateVersionSummary[] };
export type OverlayView = { head: TemplateHead; version: TemplateVersion<TemplateOverlay>; versions: TemplateVersionSummary[] };

export function useTemplatesIndex() {
  return useQuery({ queryKey: templatesKey, queryFn: () => api<TemplatesIndex>('/api/templates'), staleTime: 10_000, retry: (n) => n < 1 });
}

export function useVocabulary() {
  return useQuery({ queryKey: ['templates', 'vocabulary'], queryFn: () => api<TemplateVocabulary>('/api/templates/vocabulary'), staleTime: 300_000 });
}

export function useBase(type: ReportTypeKind, version?: number) {
  return useQuery({ queryKey: baseKey(type, version), queryFn: () => api<BaseView>(`/api/templates/bases/${type}${version ? `?version=${version}` : ''}`), retry: (n) => n < 1 });
}

/** null when the doctor has no overlay for the type (404: the base applies). */
export function useOverlay(slug: string, type: ReportTypeKind) {
  return useQuery({
    queryKey: overlayKey(slug, type),
    queryFn: async () => {
      try {
        return await api<OverlayView>(`/api/templates/overlays/${encodeURIComponent(slug)}/${type}`);
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) return null;
        throw err;
      }
    },
    retry: (n) => n < 1,
  });
}

export function useResolved(slug: string | null, type: ReportTypeKind) {
  return useQuery({ queryKey: resolvedKey(slug ?? 'base', type), queryFn: () => api<ResolvedTemplate>(`/api/templates/resolve/${encodeURIComponent(slug ?? 'base')}/${type}`), retry: (n) => n < 1 });
}

/** What a refused write said, in the server's words. */
export function problemsOf(err: unknown): { kind: 'invalid' | 'conflict' | 'not_found' | 'other'; problems: string[] } {
  if (err instanceof ApiError && err.body && typeof err.body === 'object') {
    const b = err.body as { error?: string; problems?: string[]; reason?: string };
    if (err.status === 422 && Array.isArray(b.problems)) return { kind: 'invalid', problems: b.problems };
    if (err.status === 409) return { kind: 'conflict', problems: [b.reason ?? 'The template changed underneath you. Reload and apply your change to the newest version.'] };
    if (err.status === 404) return { kind: 'not_found', problems: [b.reason ?? 'Not found.'] };
  }
  return { kind: 'other', problems: ['The change could not be saved. Nothing changed. Try again.'] };
}

function useInvalidateTemplates() {
  const client = useQueryClient();
  return async (slug?: string) => {
    await Promise.all([
      client.invalidateQueries({ queryKey: templatesKey }),
      client.invalidateQueries({ queryKey: ['templates', 'base'] }),
      client.invalidateQueries({ queryKey: ['templates', 'overlay'] }),
      client.invalidateQueries({ queryKey: ['templates', 'resolved'] }),
      client.invalidateQueries({ queryKey: doctorsKey }),
      ...(slug ? [client.invalidateQueries({ queryKey: doctorKey(slug) })] : []),
    ]);
  };
}

export function useSaveBase(type: ReportTypeKind) {
  const invalidate = useInvalidateTemplates();
  return useMutation({
    mutationFn: (vars: { body: TypeBase; changeNote: string | null }) => api<{ head: TemplateHead; version: TemplateVersion<TypeBase> }>(`/api/templates/bases/${type}`, { method: 'PUT', json: vars }),
    onSuccess: () => void invalidate(),
  });
}

export function useSaveOverlay(slug: string, type: ReportTypeKind) {
  const invalidate = useInvalidateTemplates();
  return useMutation({
    mutationFn: (vars: { body: TemplateOverlay; changeNote: string | null }) => api<{ head: TemplateHead; version: TemplateVersion<TemplateOverlay> }>(`/api/templates/overlays/${encodeURIComponent(slug)}/${type}`, { method: 'PUT', json: vars }),
    onSuccess: () => void invalidate(slug),
  });
}

export function useCloneOverlay(slug: string, type: ReportTypeKind) {
  const invalidate = useInvalidateTemplates();
  return useMutation({
    mutationFn: (fromDoctor: string) => api<{ head: TemplateHead; version: TemplateVersion<TemplateOverlay> }>(`/api/templates/overlays/${encodeURIComponent(slug)}/${type}/clone`, { method: 'POST', json: { fromDoctor } }),
    onSuccess: () => void invalidate(slug),
  });
}

export function useSetLive() {
  const invalidate = useInvalidateTemplates();
  return useMutation({
    mutationFn: (vars: { kind: 'base' | 'pack' | 'overlay'; id: string; version: number }) => api<TemplateHead>(`/api/templates/${vars.kind}/${encodeURIComponent(vars.id)}/live`, { method: 'POST', json: { version: vars.version } }),
    onSuccess: () => void invalidate(),
  });
}
